import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import { trackContactEvent } from '../utils/trackingEvents';
import TeamPreview from './TeamPreview';

const Services: React.FC = () => {
  const { config, serviceGroups, whatsappUrl, imageUrl, getPage } = useSite();
  const page = getPage('home');

  const whySection = page?.sections?.find(s => s.section_key === 'por-que-nos-escolher' || s.section_key === 'diferenciais');
  const defaultWhyItems = [
    { icon: 'fas fa-check', title: 'Equipe Altamente Qualificada', text: 'Nossos profissionais são especializados e em constante atualização nas últimas técnicas.' },
    { icon: 'fas fa-check', title: 'Tecnologia de Ponta', text: 'Investimos nos equipamentos mais modernos para diagnósticos precisos e tratamentos eficazes.' },
    { icon: 'fas fa-check', title: 'Atendimento Personalizado', text: 'Cada paciente recebe um plano de tratamento individualizado e acompanhamento cuidadoso.' },
    { icon: 'fas fa-check', title: 'Ambiente Acolhedor', text: 'Projetamos nosso espaço para seu conforto e bem-estar durante todo o tratamento.' },
  ];

  const scrollToServices = () => {
    document.getElementById('specialties-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero */}
      <section className="bg-profile-1 text-white py-5" style={{ minHeight: '50vh' }}>
        <Container className="d-flex align-items-center h-100">
          <Row className="w-100">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4">{config?.services_section_title || 'Sua Saúde Bucal em Primeiro Lugar'}</h1>
              <p className="lead mb-4">{config?.services_section_description || 'Tratamentos personalizados com tecnologia de ponta e equipe especializada'}</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="text-profile-1 fw-bold" onClick={scrollToServices}>
                  <i className="fas fa-clipboard-list me-2"></i>Todos Serviços
                </Button>
                <Button
                  variant="light" size="lg" className="text-profile-1 fw-bold"
                  href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                  onClick={trackContactEvent}
                >
                  <i className="far fa-calendar-alt me-2"></i>Agendar Consulta
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Nossas Especialidades */}
      <section id="specialties-section" className="py-5 bg-profile-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold text-profile-1 mb-3">{config?.services_header_title || 'Nossas Especialidades'}</h2>
              <div className="bg-profile-3" style={{ width: '80px', height: '4px', margin: '0 auto' }}></div>
              <p className="mt-4 text-muted">{config?.services_header_subtitle || 'Cuidamos de toda sua saúde bucal com excelência desde 2003.'}</p>
            </Col>
          </Row>
          <Row className="g-4">
            {serviceGroups.map(group => (
              <Col lg={4} md={6} key={group.id}>
                <Card className="h-100 shadow-sm border-0 hover-card">
                  <Card.Body className="p-4 text-center">
                    <div className="bg-profile-3 bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mb-3 mx-auto" style={{ width: '80px', height: '80px' }}>
                      <i className={`${group.icon_class || 'fas fa-tooth'} fs-2 text-profile-3`}></i>
                    </div>
                    <Card.Title className="text-profile-1 fw-bold mb-3">{group.description}</Card.Title>
                    <Card.Text className="text-muted mb-4">
                      {group.detailed_description || `Tratamento especializado em ${group.description.toLowerCase()}.`}
                    </Card.Text>
                    <Link
                      to={`/${group.description.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                      className="btn btn-outline-primary border-profile-1 text-profile-1"
                    >
                      Saiba mais <i className="fas fa-arrow-right ms-1"></i>
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Por que escolher nossa clínica? */}
      <section className="py-5 bg-profile-1 text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              {whySection?.section_image ? (
                <img src={imageUrl(whySection.section_image)} alt="Consultório moderno" className="img-fluid rounded shadow" />
              ) : config?.hero_image ? (
                <img src={imageUrl(config.hero_image)} alt="Consultório moderno" className="img-fluid rounded shadow" />
              ) : (
                <div className="bg-profile-3 bg-opacity-10 rounded d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                  <i className="fas fa-clinic-medical fs-1 text-profile-3"></i>
                </div>
              )}
            </Col>
            <Col lg={6}>
              <h2 className="display-5 fw-bold mb-4">{whySection?.section_title || 'Por que escolher nossa clínica?'}</h2>
              <div className="bg-profile-3 mb-4" style={{ width: '80px', height: '4px' }}></div>
              {(whySection?.items && whySection.items.length > 0 ? whySection.items : defaultWhyItems).map((item: any, i: number) => (
                <div className="mb-4" key={item.id || i}>
                  <div className="d-flex align-items-start">
                    <div className="bg-profile-3 bg-opacity-25 rounded-circle p-3 me-3">
                      <i className={`${item.item_icon || item.icon || 'fas fa-check'} text-profile-3`}></i>
                    </div>
                    <div>
                      <h5 className="fw-bold">{item.item_title || item.title}</h5>
                      <p className="text-white-50">{item.item_description || item.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      <TeamPreview />

      <style>{`
        .hover-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .hover-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(20,63,63,0.2); }
      `}</style>
    </>
  );
};

export default Services;
